import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { globalShortcutsService } from '../services/globalShortcutsService';
import useSpaceStore from '../stores/spaceStore';

function getSpaceIdFromUrl(): string | null {
  const match = window.location.pathname.match(/\/spaces\/([^/?#]+)/);
  return match?.[1] ?? null;
}

/**
 * Global space panel and navigation shortcuts.
 *
 * Cmd+\     — Toggle space sidebar
 * Cmd+J     — Toggle space chat
 * Cmd+H     — Go to my landing Desktop
 * Cmd+D     — Open Drive (entity browser) in current space
 * Cmd+N     — New chat thread (opens chat if hidden)
 *
 * Cmd+D and Cmd+N are no-ops outside a space route.
 */
export const useGlobalSpacePanelShortcuts = () => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    globalShortcutsService.register({
      id: 'global_toggle_sidebar',
      key: '\\',
      modifiers: { cmd: true },
      description: 'Toggle space sidebar',
      handler: () => {
        if (!getSpaceIdFromUrl()) return;
        const { isSidebarVisible, setSidebarVisible } = useSpaceStore.getState();
        setSidebarVisible(!isSidebarVisible);
      },
    });

    globalShortcutsService.register({
      id: 'global_toggle_chat',
      key: 'j',
      modifiers: { cmd: true },
      description: 'Toggle space chat',
      handler: () => {
        if (!getSpaceIdFromUrl()) return;
        const { isChatVisible, setIsChatVisible } = useSpaceStore.getState();
        setIsChatVisible(!isChatVisible);
      },
    });

    globalShortcutsService.register({
      id: 'global_go_home',
      key: 'h',
      modifiers: { cmd: true },
      description: 'Go to my landing Desktop',
      handler: async () => {
        try {
          const { resolveLandingDesktopRoute } = await import('../services/spaceHelpers');
          navigateRef.current(await resolveLandingDesktopRoute());
        } catch (err) {
          console.error('[useGlobalSpacePanelShortcuts] Failed to resolve landing Desktop:', err);
        }
      },
    });

    globalShortcutsService.register({
      id: 'global_open_drive',
      key: 'd',
      modifiers: { cmd: true },
      description: 'Open Drive',
      handler: async () => {
        if (!getSpaceIdFromUrl()) return;
        const windowId = await useSpaceStore.getState().actions.createWindow({
          appType: 'entityBrowser2',
          title: 'Drive',
          position: { x: 100, y: 100 },
          size: { width: 1200, height: 800 },
        });
        useSpaceStore.getState().actions.setActiveWindow(windowId);
      },
    });

    globalShortcutsService.register({
      id: 'global_new_chat_thread',
      key: 'n',
      modifiers: { cmd: true },
      description: 'New chat thread',
      handler: async () => {
        if (!getSpaceIdFromUrl()) return;

        const { isChatVisible, setIsChatVisible } = useSpaceStore.getState();
        if (!isChatVisible) {
          await setIsChatVisible(true);
        }

        const { default: userStore } = await import('../stores/userStore');
        const userId = userStore.getState().auth0Id;
        if (!userId) return;

        const { getChatStore } = await import('../components/Space/SpaceChat/store/chatStore');
        const chatStore = getChatStore(userId);
        await chatStore.getState().createThread('chat');
      },
    });

    return () => {
      globalShortcutsService.unregister('global_toggle_sidebar');
      globalShortcutsService.unregister('global_toggle_chat');
      globalShortcutsService.unregister('global_go_home');
      globalShortcutsService.unregister('global_open_drive');
      globalShortcutsService.unregister('global_new_chat_thread');
    };
  }, []);
};
